import { useLayoutEffect, useMemo, useRef } from 'react';
import { Box, ButtonBase, makeStyles } from '@material-ui/core';
import moment from 'moment';
import { TELEVISIT_STATUS } from '../../../comm/constant';
import { TimeIcon } from '../../../components/Icon';
import useResize from '../../../utils/useResize';
import bg from '../../../static/images/Group 1000001748.png';
import { i18n } from '../../../language';
import cardBg from '../../../static/images/icon_bg_orange.jpg';

const useStyle = makeStyles((theme) => ({
  card: ({ info, backgroundImage }) => {
    return {
      opacity: info?.style?.opacity,
      display: 'flex',
      alignItems: info.durationTime > 30 ? 'start' : 'center',
      justifyContent: 'space-between',
      width: '95%',
      height: '5.625rem',
      borderRadius: '10px',
      backgroundColor: info?.style?.bgColor,
      boxShadow: '0 1px 6px 0 rgba(0, 0, 0, 0.12)',
      padding: `${info.durationTime > 30 ? '0.938rem' : '0rem'} 0.625rem 0 1.125rem`,
      position: 'absolute',
      borderTop: info?.style?.border,
      borderRight: info?.style?.border,
      borderBottom: info?.style?.border,
      borderLeft: 'unset !important',
      top: '0',
      left: '0',
      backgroundImage: info?.style?.unavailable ? `url(${bg})` : backgroundImage,
      backgroundPosition: info?.style?.backgroundPosition ?? 'unset',
      backgroundSize: info?.style?.backgroundSize ?? 'unset',
      backgroundRepeat: info?.style?.backgroundRepeat ?? 'unset',
      '&::before': {
        position: 'absolute',
        top: '0',
        left: '0',
        content: '""',
        display: 'block',
        width: '0.5rem',
        height: '100%',
        backgroundColor: info?.style?.leftColor,
        borderRadius: '10px 0 0 10px',
        zIndex: '9',
      },
    };
  },
  cardInfoId: ({ info }) => ({
    fontFamily: 'NotoSans-Bold',
    color: info?.style?.color,
    fontSize: '1.5rem',
    lineHeight: '1.5rem',
  }),
  cardInfoName: ({ info }) => ({
    fontFamily: 'NotoSans-Bold',
    color: info?.style?.color,
    fontSize: '0.875rem',
    maxWidth: '15rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
  cardInfoTime: ({ info }) => ({
    fontFamily: 'NotoSans-Bold',
    color: info?.style?.color,
    fontSize: '0.75rem',
  }),
  cardInfoTimeIcon: ({ info }) => ({
    fontSize: '0.8rem',
    color: info?.style?.color,
    marginRight: '0.25rem',
  }),
  adHocFlag: ({ info }) => ({
    fontFamily: 'NotoSans-Bold',
    color: info?.style?.color,
    fontSize: '0.875rem',
    maxWidth: '15rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
}));

const TimeslotCard = ({ info, periodMemo, handleDetail, periodMarginTop }) => {
  const backgroundImage =
    info?.hocFlag === 1 && info.timeslotBookingStatusEnum !== TELEVISIT_STATUS.URGENT_BOOK ? `url(${cardBg})` : 'unset';
  const classes = useStyle({ info, backgroundImage });
  const ref = useRef();
  const resize = useResize('rem');

  // Determine location based on start and end times
  useLayoutEffect(() => {
    if (ref?.current) {
      let startMinute =
        (moment(info.startTime, 'hh:mm').valueOf() - moment(periodMemo[0], 'hh:mm').valueOf()) / (1000 * 60);
      let minutes = (moment(info.endTime, 'hh:mm').valueOf() - moment(info.startTime, 'hh:mm').valueOf()) / (1000 * 60);
      const minuteHeight =
        (document.getElementById('line1')?.offsetTop - document.getElementById('line0')?.offsetTop) / 30;
      let startingPoint = document.getElementById('line0')?.offsetTop;

      if (info.isMainBooking === 1) {
        minutes =
          (moment(info.adHocEndTime, 'HH:mm').add(1, 'd').valueOf() - moment(info.adHocStartTime, 'HH:mm').valueOf()) /
          (1000 * 60);
      }
      if (info.isMainBooking === 0) {
        startMinute = (moment('00:00', 'hh:mm').valueOf() - moment(periodMemo[0], 'hh:mm').valueOf()) / (1000 * 60);
        minutes = (moment(info.adHocEndTime, 'HH:mm').valueOf() - moment('00:00', 'HH:mm').valueOf()) / (1000 * 60);
        ref.current.style.top = `${startMinute * minuteHeight + startingPoint}px`;
        ref.current.style.height = `${minutes * minuteHeight + periodMarginTop}px`;
      } else {
        startingPoint = startingPoint + periodMarginTop;
        ref.current.style.top = `${startMinute * minuteHeight + startingPoint}px`;
        ref.current.style.height = `${minutes * minuteHeight}px`;
      }
    }
  }, [ref?.current, info, resize, periodMemo]);

  const Content = useMemo(() => {
    if (info.bookId) {
      if (info.timeslotBookingStatusEnum === TELEVISIT_STATUS.RESERVED) {
        return () => <div className={classes.cardInfoName}>{i18n.t('TELEVISIT_VERIFYING')}</div>;
      } else {
        return () => (
          <>
            {info.scheduleId && <div className={classes.cardInfoId}>{info.scheduleId}</div>}
            {info.organizer && <div className={classes.cardInfoName}>{info.organizer}</div>}
          </>
        );
      }
    } else {
      let lable =
        info.timeslotBookingStatusEnum === TELEVISIT_STATUS.UNAVAILABLE
          ? i18n.t('TELEVISIT_UNAVAILABLE')
          : i18n.t('TELEVISIT_AVAILABLE');

      return () => (
        <div className={classes.cardInfoName}>
          {info.timeslotBookingStatusEnum === TELEVISIT_STATUS.URGENT_BOOK ? '' : lable}
        </div>
      );
    }
  }, [info, classes]);

  // hide
  if (!info.display) {
    return null;
  }

  return (
    <ButtonBase
      name={info.timeslotBookingStatusEnum === TELEVISIT_STATUS.URGENT_BOOK ? 'TimeSlot_AdHoc_Card' : 'TimeSlot_Card'}
      ref={ref}
      className={classes.card}
      onClick={info.click ? handleDetail(true, info) : () => {}}
    >
      <Box display="flex" flexDirection="column" alignItems="start">
        <Content />
      </Box>
      <Box display="flex" flexDirection="column" alignItems="end">
        {info.hocFlag === 1 ? <div className={classes.cardInfoName}>{i18n.t('TELEVISIT_ADHOC_FLAG')}</div> : ''}

        <div className={classes.cardInfoTime}>
          {info.timeslotBookingStatusEnum === TELEVISIT_STATUS.URGENT_BOOK ? (
            ''
          ) : (
            <>
              <TimeIcon className={classes.cardInfoTimeIcon} />
              {info.isMainBooking === 0 ? info.adHocStartTime : info.startTime} -{' '}
              {info.isMainBooking === 1 ? info.adHocEndTime : info.endTime}
            </>
          )}
        </div>
      </Box>
    </ButtonBase>
  );
};

export default TimeslotCard;
